import React, { useState, useMemo, useRef } from "react";
import { Card, Button, Typography, Dropdown, Modal, Form, Input, Alert } from "antd";
import { externalApps } from "../constants/urls";
import { 
  ShareAltOutlined,
  ExperimentOutlined,
  ApartmentOutlined,
  GithubOutlined,
  MenuOutlined,
  SettingOutlined,
  ClusterOutlined,
} from "@ant-design/icons";
import obiLogo from "../assets/images/OBI_logo_white_transparent.png";
import brainCodeLogo from "../assets/images/brain_code_logo.png";
import neurobagelLogo from "../assets/images/neurobagel_logo.png";
import "../styles/cfa_login.css";

const REQUEST_ACCESS_EMAIL = "cfa@braininstitute.ca";

export default (function() {
  function CfaLogin() {
  const { Title, Paragraph } = Typography;
  const [activeCard, setActiveCard] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [introExpanded, setIntroExpanded] = useState(false);
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const [requestingApp, setRequestingApp] = useState(null);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [amptLeftSelected, setAmptLeftSelected] = useState("ampt");
  const darkContainerRef = useRef(null);

  const appConfigs = useMemo(() => [
  {
    id: "brain-code",
    name: "Brain-CODE",
    subtitle: "PORTAL",
    description: "Secure neuroinformatics platform for data management, sharing, and analysis",
    icon: null,
    logo: brainCodeLogo,
    logoSize: 160,
    color: "#8b5cf6",
    badge: null,
    url: externalApps.BRAIN_CODE,
    buttonText: "Access",
    buttonAction: "navigate", // "navigate" = go to URL directly, "request" = show request modal
    showLoginLink: false,
  },
  // {
  //   id: "data-discovery",
  //   name: "Data Discovery",
  //   subtitle: "PLATFORM",
  //   description: "AI-powered neuroscience research data exploration",
  //   icon: <SearchOutlined />,
  //   color: "#8b5cf6",
  //   badge: "POPULAR",
  //   url: externalApps.DATA_DISCOVERY,
  //   buttonText: "Request Access",
  //   buttonAction: "navigate",
  //   showLoginLink: false,
  // },
  {
    id: "neurobagel",
    name: "Neurobagel",
    subtitle: "QUERY TOOL",
    description: "Public neuroscience data search and discovery",
    icon: null,
    logo: neurobagelLogo,
    color: "#cc5098",
    badge: null,
    url: externalApps.NEUROBAGEL,
    buttonText: "Access",
    buttonAction: "navigate",
    showLoginLink: false,
  },
  {
    id: "faranak-clustering",
    name: "IM-Biotype Toolbox",
    subtitle: "DESKTOP TOOL",
    description:
      "Desktop clustering workflow from CfA methods tutorials (Farzan Lab).",
    icon: <ClusterOutlined />,
    logo: null,
    color: "#8b5cf6",
    badge: null,
    url: externalApps.FARANAK_CLUSTERING_GITHUB,
    buttonText: "View on GitHub",
    buttonAction: "navigate",
    showLoginLink: false,
  },
  {
    id: "neurofl",
    name: "Federated Learning",
    subtitle: "NEUROFL",
    description: "Collaborative machine learning platform",
    // Use network-style icon for Federated Learning
    icon: <ShareAltOutlined />,
    color: "#cc5098",
    badge: "REGISTERED ONLY",
    url: externalApps.NEUROFL,
    buttonText: "Request Access",
    buttonAction: "request",
    showLoginLink: true,
  },
  {
    id: "cfa-github",
    name: "CfA GitHub",
    subtitle: "REPOSITORY",
    description: "Open-source code and collaboration",
    icon: <GithubOutlined />,
    color: "#8b5cf6",
    badge: null,
    url: externalApps.CFA_GITHUB,
    buttonText: "View on Github",
    buttonAction: "navigate",
    showLoginLink: false,
  },
  {
    id: "ampt-left",
    name: "AMPT/LEFT",
    subtitle: "AMPT / LEFT",
    description: "Select AMPT or LEFT, then request access or use Login to open the app in a new tab.",
    icon: null,
    logo: null,
    color: "#ec4899",
    badge: null,
    buttonAction: "request",
    showLoginLink: true,
    subApps: [
      {
        id: "ampt",
        name: "AMPT",
        description: "Amplifying mental health precision trials for advanced research.",
        icon: <ExperimentOutlined />,
        logo: null,
        url: externalApps.AMPT,
      },
      {
        id: "left",
        name: "LEFT",
        description: "LEFT platform for advanced research access and workflows.",
        icon: <ApartmentOutlined />,
        logo: null,
        url: externalApps.LEFT,
      },
    ],
  },
], []);

  /** Opens URL in a new tab only — never replaces the current document (avoids broken Back / stale React after return). */
  const handleAccessApp = (appUrl) => {
    if (!appUrl) return;
    try {
      const opened = window.open(appUrl, "_blank", "noopener,noreferrer");
      if (opened != null) return;
    } catch {
      /* fall through */
    }
    const a = document.createElement("a");
    a.href = appUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const adminMenuItems = [
    {
      key: 'admin-portal',
      label: 'Admin Portal',
      icon: <SettingOutlined />,
      disabled: true, // Not active yet
    },
  ];

  const getLoginUrl = (app) => app.url;

  const getAmptLeftSelectedSub = (app) =>
    app.subApps?.find((s) => s.id === amptLeftSelected) || app.subApps?.[0];

  return (
    <div className="dark-container" ref={darkContainerRef}>
      {/* Grid Background */}
      <div className="grid-background"></div>
      
      {/* Glowing Orbs */}
      <div className="glow-orb orb-1"></div>
      <div className="glow-orb orb-2"></div>
      <div className="glow-orb orb-3"></div>

      {/* Sticky Top Bar - Logo + menu, prevents content overlap on scroll */}
      <div className="dark-top-bar">
        <div className="obi-logo-wrapper">
          <img 
            src={obiLogo} 
            alt="Ontario Brain Institute" 
            className="obi-logo"
          />
        </div>
        <div className="admin-menu-wrapper">
          <Dropdown
            menu={{ items: adminMenuItems }}
            trigger={['click']}
            placement="bottomRight"
            open={adminMenuOpen}
            onOpenChange={setAdminMenuOpen}
          >
            <Button 
              type="text" 
              icon={<MenuOutlined />} 
              className="admin-menu-button"
              size="large"
            />
          </Dropdown>
        </div>
      </div>

      {/* Content */}
      <div className="dark-content">
        <div className="dark-header">
          <Title level={1} className="dark-title">CENTRE FOR ANALYTICS</Title>
          <Paragraph className="dark-tagline">
            Neuroanalytics Applications for Brain Discovery
          </Paragraph>
          <div className="dark-hero-intro">
            <div className="dark-header-intro">
              <Paragraph className="dark-intro-text">
                {introExpanded ? (
                  <>
                    Welcome to the Centre for Analytics (CfA) – Advancing Neuroanalytics for Brain Health.
                    At the heart of neuroscience innovation, the Centre for Analytics (CfA) drives the development of cutting-edge neuroanalytics and AI/ML technologies. By harnessing multi-modal data, we empower researchers and clinicians to detect, predict, and treat brain disorders—from mental health and neurodevelopmental conditions to dementia and brain injuries—with unprecedented precision. Our mission is to bridge complex data and advanced algorithms, creating transformative tools for early risk detection, accurate diagnosis, and personalized treatment. Through upskilling the next generation of neuro data scientists, accelerating access to high-quality data and cloud computing, and translating neuroanalytical methods ethically, the CfA is shaping the future of brain health.{' '}
                    <a
                      href="https://braininstitute.ca/research-data-sharing/centre-for-analytics"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dark-intro-link"
                    >
                      Learn more about the CfA
                    </a>
                    .
                  </>
                ) : (
                  <>
                    Welcome to the Centre for Analytics (CfA) – Advancing Neuroanalytics for Brain Health.
                    At the heart of neuroscience innovation, the Centre for Analytics (CfA) drives the development of cutting-edge neuroanalytics and AI/ML technologies. By harnessing multi-modal data, we empower researchers and clinicians to detect, predict, and treat brain disorders—from mental health and neurodevelopmental conditions to dementia and brain injuries—with unprecedented precision. Our mission is to bridge complex data and advanced algorithms...
                  </>
                )}
              </Paragraph>
              {!introExpanded ? (
                <Button
                  type="link"
                  className="dark-intro-read-more"
                  onClick={() => setIntroExpanded(true)}
                >
                  Show more
                </Button>
              ) : (
                <Button
                  type="link"
                  className="dark-intro-read-more"
                  onClick={() => setIntroExpanded(false)}
                >
                  Show less
                </Button>
              )}
            </div>
          </div>

          <div className="dark-intro-lead">
            <Paragraph className="dark-intro-lead-text">
              Below, you can explore our collection of neuroanalytics and neuroscience data apps, designed to fuel discovery and innovation. Together, we're charting new pathways to improve lives through science and technology.
            </Paragraph>
          </div>
        </div>

        <div className="dark-cards-container">
          {appConfigs.map((app, index) => (
            <div
              key={app.id}
              className={`dark-card-wrapper ${activeCard === app.id ? "active" : ""}`}
              onMouseEnter={() => setActiveCard(app.id)}
              onMouseLeave={() => setActiveCard(null)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <Card
                className={`dark-app-card ${app.id === "ampt-left" ? "dark-app-card--ampt" : ""}`}
                bordered={false}
              >
                {app.badge && (
                  <span
                    className="dark-card-badge-ribbon"
                    style={{
                      backgroundColor: app.color,
                    }}
                  >
                    {app.badge}
                  </span>
                )}
                <div className="dark-card-glow" style={{ background: app.color }}></div>
                
                <div
                  className={`dark-card-icon ${
                    app.id === "ampt-left" ? "dark-card-icon--ampt" : ""
                  }`}
                  style={{ color: app.color }}
                >
                  {app.id === "ampt-left" && app.subApps ? (
                    <div className="dark-amptleft-icons dark-amptleft-icons--card-icon">
                      {app.subApps.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          className={`dark-amptleft-icon ${
                            amptLeftSelected === sub.id ? "dark-amptleft-icon--active" : ""
                          }`}
                          style={{ color: app.color, borderColor: `${app.color}55` }}
                          title={`Select ${sub.name}`}
                          onClick={() => setAmptLeftSelected(sub.id)}
                        >
                          <div className="dark-amptleft-icon-glyph">{sub.icon || null}</div>
                          <div className="dark-amptleft-icon-label">{sub.name}</div>
                        </button>
                      ))}
                    </div>
                  ) : app.logo ? (
                    <img
                      src={app.logo}
                      alt={app.name}
                      className="dark-card-logo"
                      style={
                        app.logoSize
                          ? { width: app.logoSize, height: app.logoSize }
                          : undefined
                      }
                    />
                  ) : (
                    app.icon || null
                  )}
                </div>

                <div className="dark-card-text">
                  <Title level={3} className="dark-card-title">{app.name}</Title>
                  <span className="dark-card-subtitle" style={{ color: app.color }}>
                    {app.subtitle}
                  </span>
                  <Paragraph className="dark-card-desc">{app.description}</Paragraph>
                </div>

                <div className="dark-card-actions">
                  {app.subApps ? (
                    <>
                      {getAmptLeftSelectedSub(app) ? (
                        <Button
                          type="primary"
                          className="dark-card-btn"
                          style={{
                            background: app.color,
                            borderColor: app.color,
                            boxShadow: `0 0 20px ${app.color}50`,
                          }}
                          onClick={() => {
                            const sub = getAmptLeftSelectedSub(app);
                            if (sub) {
                              setRequestingApp(sub);
                              setRequestError(null);
                              setRequestAccessOpen(true);
                            }
                          }}
                        >
                          Request — {getAmptLeftSelectedSub(app).name}
                        </Button>
                      ) : null}
                    </>
                  ) : (
                    <Button
                      type="primary"
                      className="dark-card-btn"
                      style={{ 
                        background: app.color,
                        borderColor: app.color,
                        boxShadow: `0 0 20px ${app.color}50`
                      }}
                      onClick={() => {
                        if (app.buttonAction === "request") {
                          setRequestingApp(app);
                          setRequestError(null);
                          setRequestAccessOpen(true);
                        } else {
                          handleAccessApp(app.url);
                        }
                      }}
                    >
                      {app.buttonText}
                    </Button>
                  )}
                  {app.showLoginLink &&
                    (app.subApps ? (
                      <a
                        className="dark-card-login"
                        href={getAmptLeftSelectedSub(app)?.url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (!getAmptLeftSelectedSub(app)?.url) e.preventDefault();
                        }}
                      >
                        Login — {getAmptLeftSelectedSub(app)?.name}
                      </a>
                    ) : (
                      <a
                        className="dark-card-login"
                        href={getLoginUrl(app) || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (!getLoginUrl(app)) e.preventDefault();
                        }}
                      >
                        Login
                      </a>
                    ))}
                </div>
              </Card>
            </div>
          ))}
        </div>

        <div className="dark-footer">
          <span className="dark-footer-text">© 2026 Ontario Brain Institute</span>
          <span className="dark-footer-separator">·</span>
          <a
            href="/OBI_Cloud_Platform_Terms_of_Use.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="dark-footer-link"
          >
            Platform Terms of Use
          </a>
        </div>
      </div>

      <Modal
        open={requestAccessOpen}
        title={requestingApp ? `Request access to ${requestingApp.name}` : "Request access"}
        onCancel={() => {
          setRequestAccessOpen(false);
          setRequestError(null);
        }}
        footer={null}
        className="dark-request-modal"
      >
        {requestError ? (
          <Alert
            type="error"
            message={requestError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form
          layout="vertical"
          onFinish={async (values) => {
            const subject = `Request access to ${requestingApp?.name || "CfA application"}`;
            const bodyLines = [
              `Application: ${requestingApp?.name || ""}`,
              `Name: ${values.name}`,
              `Email: ${values.email}`,
              `Affiliation: ${values.affiliation}`,
              "",
              "Question / details:",
              values.question || "",
            ];
            setRequestSubmitting(true);
            setRequestError(null);
            try {
              const apiUrl = process.env.REACT_APP_ACCESS_REQUEST_API_URL?.trim();
              if (apiUrl) {
                const res = await fetch(apiUrl, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    applicationName: requestingApp?.name || "",
                    name: values.name,
                    email: values.email,
                    affiliation: values.affiliation,
                    question: values.question || "",
                  }),
                });
                let data = {};
                try {
                  data = await res.json();
                } catch {
                  /* non-JSON body */
                }
                if (!res.ok) {
                  throw new Error(
                    data.error || res.statusText || "Failed to send request"
                  );
                }
              } else {
                const body = bodyLines.join("\n");
                const mailto = `mailto:${REQUEST_ACCESS_EMAIL}?subject=${encodeURIComponent(
                  subject
                )}&body=${encodeURIComponent(body)}`;
                window.location.href = mailto;
              }
              setRequestAccessOpen(false);
            } catch (err) {
              setRequestError(
                err?.message || "Something went wrong. Please try again."
              );
            } finally {
              setRequestSubmitting(false);
            }
          }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Affiliation"
            name="affiliation"
            rules={[{ required: true, message: "Please enter your affiliation" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Question / details"
            name="question"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <div className="dark-request-actions">
              <button
                type="button"
                className="dark-request-cancel"
                disabled={requestSubmitting}
                onClick={() => {
                  setRequestAccessOpen(false);
                  setRequestError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="dark-request-submit"
                disabled={requestSubmitting}
              >
                {requestSubmitting ? "Sending…" : "Submit Request"}
              </button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
  }
  return CfaLogin;
})();
